import { ProfileCard } from './ProfileCard';
import { OrganizationCard } from './OrganizationCard';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BoostProfileModal } from '@/components/profile/BoostProfileModal';

interface Profile {
  id: string;
  full_name: string | null;
  headline: string | null;
  avatar_url: string | null;
  category_id: string | null;
  is_hiring: boolean | null;
  artist_categories: {
    name: string;
  } | null;
  languages: string[] | null;
  availability_status: string | null;
  experience_level: string | null;
}

interface Organization {
  id: string;
  name: string;
  description: string | null;
  location: string | null;
  website: string | null;
  type: string;
}

interface DirectoryResultsProps {
  selectedDirectoryCategory: string | null;
  filteredProfiles: Profile[];
  filteredOrganizations: Organization[];
}

export const DirectoryResults = ({
  selectedDirectoryCategory,
  filteredProfiles,
  filteredOrganizations
}: DirectoryResultsProps) => {
  const showProfiles = !selectedDirectoryCategory || selectedDirectoryCategory === 'people';
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        console.log('Current user ID:', user.id);
        setCurrentUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Only filter out the current user's profile if they're not a casting director
  const displayProfiles = currentUserId 
    ? filteredProfiles.filter(profile => {
        const isCastingDirector = profile.artist_categories?.name === 'Casting Directors';
        // If the profile is a casting director, always show it
        // Otherwise, filter out the current user's profile
        return isCastingDirector || profile.id !== currentUserId;
      })
    : filteredProfiles;

  console.log('Current user ID:', currentUserId);
  console.log('Showing profiles:', showProfiles);
  console.log('Number of profiles:', displayProfiles.length);
  console.log('Raw filtered profiles:', filteredProfiles);
  console.log('Display profiles after filtering:', displayProfiles);

  // If no category is selected, show all content in order
  if (!selectedDirectoryCategory) {
    return (
      <div className="space-y-12">
        {/* People Section */}
        {displayProfiles.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">People</h2>
              <Button
                onClick={() => setShowBoostModal(true)}
                className="bg-[#9b87f5] hover:bg-[#7E69AB] text-white"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Vai um Boost ao teu Portfólio?
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 sm:gap-8">
              {displayProfiles.map((profile) => (
                <ProfileCard
                  key={profile.id}
                  id={profile.id}
                  fullName={profile.full_name}
                  headline={profile.headline}
                  avatarUrl={profile.avatar_url}
                  categoryName={profile.artist_categories?.name}
                  isHiring={profile.is_hiring || false}
                  languages={profile.languages || []}
                  availabilityStatus={profile.availability_status || 'available'}
                  experienceLevel={profile.experience_level || undefined}
                />
              ))}
            </div>
          </div>
        )}

        {/* Organizations by Type */}
        {['theatre', 'museum', 'gallery', 'studio'].map((type) => {
          const typeOrganizations = filteredOrganizations.filter(org => org.type === type);
          if (typeOrganizations.length === 0) return null;

          const typeLabels: Record<string, string> = {
            theatre: 'Theatres',
            museum: 'Museums',
            gallery: 'Exhibition Sites',
            studio: 'Production Studios'
          };

          return (
            <div key={type} className="mt-12">
              <h2 className="text-2xl font-semibold mb-6">{typeLabels[type]}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 sm:gap-8">
                {typeOrganizations.map((org) => (
                  <OrganizationCard
                    key={org.id}
                    name={org.name}
                    description={org.description}
                    location={org.location}
                    website={org.website}
                  />
                ))}
              </div>
            </div>
          );
        })}

        <BoostProfileModal 
          isOpen={showBoostModal}
          onClose={() => setShowBoostModal(false)}
          userRole="talent"
        />
      </div>
    );
  }

  // Show filtered results when a category is selected
  if ((showProfiles && displayProfiles.length === 0) || 
      (!showProfiles && filteredOrganizations.length === 0)) {
    return (
      <div className="col-span-full text-center py-8 text-muted-foreground">
        No results found matching your search criteria
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 sm:gap-8">
        {showProfiles ? (
          displayProfiles.map((profile) => (
            <ProfileCard
              key={profile.id}
              id={profile.id}
              fullName={profile.full_name}
              headline={profile.headline}
              avatarUrl={profile.avatar_url}
              categoryName={profile.artist_categories?.name}
              isHiring={profile.is_hiring || false}
              languages={profile.languages || []}
              availabilityStatus={profile.availability_status || 'available'}
              experienceLevel={profile.experience_level || undefined}
            />
          ))
        ) : (
          filteredOrganizations.map((org) => (
            <OrganizationCard
              key={org.id}
              name={org.name}
              description={org.description}
              location={org.location}
              website={org.website}
            />
          ))
        )}
      </div>
      <BoostProfileModal 
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        userRole="talent"
      />
    </div>
  );
};
